import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';

import Fader from '../../components/Loaders/Fader';
import InputItem from '../../components/Forms/InputItem/InputItem';
import CustomSelect from '../../components/Forms/CustomSelect/CustomSelect';

import { FEEDBACK_TYPES, IMPORTANCE_TYPES, SOCIAL_SERVICES_SUBTYPES } from '../../../util/constants/AppConstants';
import { checkPostalCode, formatUnixDate } from '../../../util/HelperFunctions';
import { createFeedback } from '../../../http/feedbackAPI';
import { setNotify } from '../../../datastore/actions/actionActions';

import NextIcon from '../../../assets/images/icons/next.svg';
import './PendingMessages.scss';

const PendingMessages = () => {
  const dispatch = useDispatch();

  const pendingFeedbackState = useSelector((state) => state.data.pendingFeedback);

  const [activeFeedbackId, setActiveFeedbackId] = useState(null);
  const [activeFeedback, setActiveFeedback] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [postalCode, setPostalCode] = useState('');
  const [type, setType] = useState('');
  const [subtype, setSubtype] = useState('');
  const [importance, setImportance] = useState(IMPORTANCE_TYPES[2].value);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const config = useSelector((state) => state.config.config);

  const { userInfo, handleGetFeedbackData, handleGetPendingFeedbackData, setDeleteRequest, setDeleteId } =
    useOutletContext();

  useEffect(() => {
    if (pendingFeedbackState.length > 0) {
      setActiveFeedbackId(pendingFeedbackState[0].id);
    }
  }, [pendingFeedbackState]);

  useEffect(() => {
    if (pendingFeedbackState) {
      const feedback = pendingFeedbackState.find((elem) => elem.id === activeFeedbackId);

      if (feedback) {
        setActiveFeedback(feedback);
      }
    }
  }, [pendingFeedbackState, activeFeedbackId]);

  useEffect(() => {
    if (activeFeedback) {
      setName(activeFeedback.name);
      setPhone(activeFeedback.phone);
      setEmail(activeFeedback.email);
      const addresses = activeFeedback.address;
      setAddresses(addresses);
      if (addresses && addresses.length > 0) {
        setAddress(addresses[0]);
      } else {
        setAddress('');
      }
      setPostalCode(activeFeedback.postal_code);
      setType(activeFeedback.type);
      setSubtype(activeFeedback.subtype);
      setImportance(activeFeedback.importance_suggested);
      setDescription(activeFeedback.description);
    }
  }, [activeFeedback]);

  const getNewIndex = (next) => {
    const currentIndex = pendingFeedbackState.findIndex((elem) => elem.id === activeFeedbackId);

    const newIndex = next ? currentIndex + 1 : currentIndex - 1;
    return newIndex;
  };

  const checkIsActive = (next) => {
    const newIndex = getNewIndex(next);
    return newIndex >= 0 && newIndex < pendingFeedbackState.length;
  };

  const goToMessage = (next) => {
    const newIndex = getNewIndex(next);
    if (newIndex >= 0 && newIndex < pendingFeedbackState.length) {
      setActiveFeedbackId(pendingFeedbackState[newIndex].id);
    }
  };

  const checkIsValid = () => {
    if (name && postalCode && type) {
      return true;
    }

    return false;
  };

  const handleApprove = async () => {
    if (checkIsValid()) {
      if (checkPostalCode(postalCode)) {
        setSaving(true);

        const res = await createFeedback(
          config,
          userInfo.id,
          null,
          name,
          phone,
          email,
          address,
          postalCode.toUpperCase().trim(),
          type,
          type === 'Social Services' ? subtype : '',
          importance,
          description,
          '',
          'Email',
          activeFeedback.sentiment,
          activeFeedback.date_utc,
          activeFeedbackId,
          activeFeedback.email_id,
        );

        if (res.status === 201) {
          dispatch(
            setNotify({
              status: true,
              type: 'success',
              title: 'Success',
              message: res.data.message,
            }),
          );

          handleGetPendingFeedbackData(userInfo.id);
          handleGetFeedbackData(userInfo.id, userInfo.role);
        } else {
          dispatch(
            setNotify({
              status: true,
              type: 'error',
              title: 'Error',
              message: res.data.message,
            }),
          );
        }
        setSaving(false);
      } else {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message:
              'Invalid postal code format. Please enter a 6-character code in the format C#C#C# (C - letter, # - digit)',
          }),
        );
      }
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Fill in all required fields',
        }),
      );
    }
  };

  const handleDelete = async () => {
    setDeleteRequest(true);
    setDeleteId(activeFeedbackId);
  };

  return (
    <div className="messages">
      {pendingFeedbackState && pendingFeedbackState.length > 0 && activeFeedback ? (
        <div className="messages__view">
          <div className="messages__header">
            <h2 className="messages__title">
              <div>
                <img
                  className={`messages__arrow ${checkIsActive(false) ? '' : 'inactive'}`}
                  src={NextIcon}
                  onClick={() => goToMessage(false)}
                />
                <img
                  className={`messages__arrow ${checkIsActive(true) ? '' : 'inactive'}`}
                  src={NextIcon}
                  onClick={() => goToMessage(true)}
                />
              </div>
              Email {pendingFeedbackState.findIndex((elem) => elem.id === activeFeedbackId) + 1} out of{' '}
              {pendingFeedbackState.length}
            </h2>
            {saving ? (
              <Fader />
            ) : (
              <div className="messages__actions">
                <div className="create-btn secondary" onClick={handleDelete}>
                  Delete
                </div>
                <div className="create-btn" onClick={handleApprove}>
                  Approve
                </div>
                <img
                  className={`messages__arrow ${checkIsActive(false) ? '' : 'inactive'}`}
                  src={NextIcon}
                  onClick={() => goToMessage(false)}
                />
                <img
                  className={`messages__arrow ${checkIsActive(true) ? '' : 'inactive'}`}
                  src={NextIcon}
                  onClick={() => goToMessage(true)}
                />
              </div>
            )}
          </div>
          <div className="messages__form">
            <InputItem label="Name *" type="text" onChange={(e) => setName(e.target.value)} value={name} />
            <InputItem label="Phone" type="text" onChange={(e) => setPhone(e.target.value)} value={phone} />
            <InputItem label="Email" type="email" onChange={(e) => setEmail(e.target.value)} value={email} />
            {addresses && addresses.length > 1 ? (
              <CustomSelect
                label="Address"
                options={addresses.map((address) => ({ label: address, value: address }))}
                selectedOption={address}
                setSelectedOption={setAddress}
              />
            ) : (
              <InputItem label="Address" type="text" onChange={(e) => setAddress(e.target.value)} value={address} />
            )}
            <InputItem
              label="Postal Code *"
              type="text"
              onChange={(e) => setPostalCode(e.target.value)}
              value={postalCode}
            />
            <CustomSelect label="Type *" options={FEEDBACK_TYPES} selectedOption={type} setSelectedOption={setType} />
            {type === 'Social Services' && (
              <CustomSelect
                label="Subtype"
                options={SOCIAL_SERVICES_SUBTYPES}
                selectedOption={subtype}
                setSelectedOption={setSubtype}
              />
            )}
            <CustomSelect
              label="Importance"
              options={IMPORTANCE_TYPES}
              selectedOption={importance}
              setSelectedOption={setImportance}
            />
            <InputItem
              label="Description"
              onChange={(e) => setDescription(e.target.value)}
              value={description}
              isText={true}
            />
          </div>
          <div className="messages__original">
            <h2 className="messages__title">Original email</h2>
            <p className="messages__date">{formatUnixDate(activeFeedback.date_utc, true)}</p>
            <p className="messages__info">
              <span>From:</span> {activeFeedback.sender}
            </p>
            <p className="messages__info">
              <span>Subject:</span> {activeFeedback.subject}
            </p>
            <p className="messages__text">{activeFeedback.body_text}</p>
          </div>
        </div>
      ) : (
        <div className="messages__no-data">
          <span>No Pending Emails</span>
        </div>
      )}
    </div>
  );
};

export default PendingMessages;
